import Color from "color";
import PropTypes from "prop-types";
import React, { Fragment } from "react";
import regression from "regression";
import { useTheme } from "styled-components";
import {
  getPredictionInterval,
  setLocalStorageItem
} from "../../utils/listings";
import IconHouse from "../icons/IconHouse";
import IconTrendline from "../icons/IconTrendline";
import { Area, Dot, DynamicLine, Line, SVG, ScatterPlot } from "./ScatterPlot";
import {
  Checkbox,
  Legend,
  LegendIcon,
  LegendSubjectPropertyIcon,
  Legends,
  Placeholder
} from "./styled/report-chart";

function AveragePricePerSqftChart({
  width,
  height,
  listings,
  subjectProperty,
  graphType,
  hoverListingId,
  previewListingId,
  setPreviewListingId,
  hasLabel,
  horizontalLegends,
  showSubjectPropertyIcon,
  setShowSubjectPropertyIcon,
  showTrendline,
  setShowTrendline,
  showLegend,
  showControls
}) {
  const theme = useTheme();
  const areaType = graphType === "home" ? "sqft" : "lotSize";
  const filteredListings = listings.filter(
    (listing) => listing[areaType] && listing.adjustedPrice
  );
  const hoverListing = filteredListings.find(
    (listing) => listing.recordId === hoverListingId
  );
  const previewListing = filteredListings.find(
    (listing) => listing.recordId === previewListingId
  );
  const soldListings = filteredListings.filter((listing) => listing.soldPrice);
  const sqfts = [
    ...filteredListings.map((listing) => listing[areaType]),
    subjectProperty[areaType]
  ].filter(Boolean);
  const minSqft = sqfts.length ? Math.min(...sqfts) : 0;
  const maxSqft = sqfts.length ? Math.max(...sqfts) : 0;
  const pricesAndSqft = soldListings.map((listing) => [
    listing[areaType],
    listing.adjustedPrice
  ]);
  const results = regression.linear(pricesAndSqft);
  const [slope, yIntercept] = results.equation;
  const minBounds = 0;
  const maxBounds = 10000000;
  const regressionLine = [
    ...results.points,
    results.predict(minBounds),
    results.predict(maxBounds)
  ];
  const prediction = getPredictionInterval({
    actual: pricesAndSqft,
    predicted: results.points
  });
  const predictionInterval = prediction === Infinity ? 0 : prediction;
  const predictionLineUpperBounds = {
    x1: maxBounds,
    y1: slope * maxBounds + yIntercept + predictionInterval,
    x2: minBounds,
    y2: yIntercept + predictionInterval
  };
  const predictionLineLowerBounds = {
    x1: maxBounds,
    y1: slope * maxBounds + yIntercept - predictionInterval,
    x2: minBounds,
    y2: yIntercept - predictionInterval
  };
  const subjectPropertyEstimatedPrice = subjectProperty[areaType]
    ? results.predict(subjectProperty[areaType])[1]
    : 0;
  const minPrices = [
    ...filteredListings.map((listing) => listing.adjustedPrice),
    subjectPropertyEstimatedPrice,
    subjectProperty.priceRange.min
  ].filter(Boolean);
  const minPrice = minPrices.length ? Math.min(...minPrices) : 0;
  const maxPrices = [
    ...filteredListings.map((listing) => listing.adjustedPrice),
    subjectPropertyEstimatedPrice,
    subjectProperty.priceRange.max
  ].filter(Boolean);
  const maxPrice = maxPrices.length ? Math.max(...maxPrices) : 0;
  const showSubjectProperty = !!subjectProperty[areaType];
  const showPredictionLine = !!regressionLine.length;

  return (
    <>
      {!!filteredListings.length && (
        <ScatterPlot
          width={width}
          height={height}
          minX={minSqft}
          maxX={maxSqft}
          minY={minPrice}
          maxY={maxPrice}
          xLabel="Square feet">
          {showTrendline && showPredictionLine && (
            <>
              <DynamicLine
                data={regressionLine}
                color={theme.styleGuide.colors.gray[600]}
              />
              <Area
                line1={predictionLineUpperBounds}
                line2={predictionLineLowerBounds}
                color={Color(theme.styleGuide.colors.gray[600])
                  .alpha(0.1)
                  .toString()}
              />
            </>
          )}
          {showSubjectPropertyIcon && showSubjectProperty && (
            <>
              <Line
                x1={subjectProperty[areaType]}
                y1={maxPrice}
                x2={subjectProperty[areaType]}
                y2={minPrice}
                color={theme.styleGuide.colors.blue[200]}
                dash="3, 3"
              />

              {/* Max */}
              {!!subjectProperty.priceRange.max && (
                <>
                  <Line
                    x1={subjectProperty[areaType]}
                    y1={
                      subjectProperty.priceRange.max ||
                      subjectPropertyEstimatedPrice ||
                      maxPrice
                    }
                    x2={subjectProperty[areaType]}
                    y2={
                      subjectProperty.priceRange.min ||
                      subjectPropertyEstimatedPrice ||
                      minPrice
                    }
                    color={theme.styleGuide.colors.suite[600]}
                    width={2}
                  />
                  <Dot
                    radius={12}
                    x={subjectProperty[areaType]}
                    y={
                      subjectProperty.priceRange.max ||
                      subjectPropertyEstimatedPrice ||
                      maxPrice
                    }
                    outlineColor={theme.styleGuide.colors.suite[600]}
                    color={theme.styleGuide.colors.suite[500]}
                  />
                  <SVG
                    width={12}
                    height={12}
                    x={subjectProperty[areaType]}
                    y={
                      subjectProperty.priceRange.max ||
                      subjectPropertyEstimatedPrice ||
                      maxPrice
                    }
                    color={theme.styleGuide.colors.white}
                    transform="translate(-6, -6.5)"
                    paths={[
                      "M5.561.165a.667.667 0 01.878 0l5.333 4.667c.145.126.228.31.228.501v6a.667.667 0 01-.667.667H.667A.667.667 0 010 11.333v-6c0-.192.083-.375.228-.501L5.56.165z"
                    ]}
                  />
                </>
              )}

              {/* Min */}
              <>
                <Dot
                  radius={12}
                  x={subjectProperty[areaType]}
                  y={
                    subjectProperty.priceRange.min ||
                    subjectPropertyEstimatedPrice ||
                    minPrice
                  }
                  outlineColor={theme.styleGuide.colors.suite[600]}
                  color={theme.styleGuide.colors.suite[500]}
                />
                <SVG
                  width={12}
                  height={12}
                  x={subjectProperty[areaType]}
                  y={
                    subjectProperty.priceRange.min ||
                    subjectPropertyEstimatedPrice ||
                    minPrice
                  }
                  color={theme.styleGuide.colors.white}
                  transform="translate(-6, -6.5)"
                  paths={[
                    "M5.561.165a.667.667 0 01.878 0l5.333 4.667c.145.126.228.31.228.501v6a.667.667 0 01-.667.667H.667A.667.667 0 010 11.333v-6c0-.192.083-.375.228-.501L5.56.165z"
                  ]}
                />
              </>
            </>
          )}
          {filteredListings.map((listing, index) => (
            <Fragment key={listing.recordId}>
              <Dot
                x={listing[areaType]}
                y={listing.adjustedPrice}
                outlineColor={listing.statusColor}
                color={Color(listing.statusColor).lighten(0.2).toString()}
                zIndex={hoverListingId === listing.recordId ? 100 : 0}
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewListingId(listing.recordId);
                }}
                hasLabel={hasLabel}
                label={index + 1}
              />
            </Fragment>
          ))}
          {!!(previewListing || hoverListing) && (
            <>
              <Dot
                x={(previewListing || hoverListing)[areaType]}
                y={(previewListing || hoverListing).adjustedPrice}
                radius={12}
                outlineColor={(previewListing || hoverListing).statusColor}
                color={Color((previewListing || hoverListing).statusColor)
                  .lighten(0.2)
                  .toString()}
              />
              {!!previewListing && (
                <SVG
                  width={12}
                  height={12}
                  x={previewListing[areaType]}
                  y={previewListing.adjustedPrice}
                  color={theme.styleGuide.colors.white}
                  transform="translate(-6, -5)"
                  paths={[
                    "M12.246.188a.75.75 0 01.066 1.058l-7.5 8.5a.75.75 0 01-1.092.034l-3.5-3.5a.75.75 0 111.06-1.06l2.936 2.935L11.188.254a.75.75 0 011.058-.066z"
                  ]}
                />
              )}
            </>
          )}
        </ScatterPlot>
      )}
      {!filteredListings.length && <Placeholder />}
      {(showSubjectProperty || showPredictionLine) && (
        <Legends horizontalLegends={horizontalLegends}>
          {showSubjectProperty && (
            <Legend>
              {showControls && (
                <Checkbox
                  variant="live-primary"
                  shadowSize={0.1}
                  checked={showSubjectPropertyIcon}
                  onChange={() => {
                    setShowSubjectPropertyIcon(!showSubjectPropertyIcon);
                    setLocalStorageItem(
                      "show-subject-property",
                      !showSubjectPropertyIcon
                    );
                  }}
                />
              )}
              <LegendSubjectPropertyIcon>
                <IconHouse />
              </LegendSubjectPropertyIcon>
              Subject Property
            </Legend>
          )}
          {showLegend && showPredictionLine && (
            <Legend>
              {showControls && (
                <Checkbox
                  variant="live-primary"
                  shadowSize={0.1}
                  checked={showTrendline}
                  onChange={() => setShowTrendline(!showTrendline)}
                />
              )}
              <LegendIcon>
                <IconTrendline />
              </LegendIcon>
              Trendline (based on SOLD listings only)
            </Legend>
          )}
        </Legends>
      )}
    </>
  );
}

AveragePricePerSqftChart.defaultProps = {
  setPreviewListingId: () => {},
  showSubjectPropertyIcon: false,
  showTrendline: false,
  setShowSubjectPropertyIcon: () => {},
  setShowTrendline: () => {},
  showLegend: true,
  showControls: true
};

AveragePricePerSqftChart.propTypes = {
  graphType: PropTypes.string,
  hoverListingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  previewListingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setPreviewListingId: PropTypes.func,
  width: PropTypes.number,
  height: PropTypes.number,
  showSubjectPropertyIcon: PropTypes.bool,
  setShowSubjectPropertyIcon: PropTypes.func,
  showTrendline: PropTypes.bool,
  setShowTrendline: PropTypes.func,
  showLegend: PropTypes.bool,
  showControls: PropTypes.bool
};

export default AveragePricePerSqftChart;
